import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Car, Menu, User, LogOut, Calendar, MessageSquare, LayoutDashboard, List, Settings, BarChart3 } from "lucide-react";
import PremiumIcon from "./ui/PremiumIcon";
import NotificationsBell from "./NotificationsBell";
import LanguageSwitcher from "./LanguageSwitcher";
import { useState } from "react";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";

export default function Navbar() {
  const { user, isAuthenticated, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const navLinks = [
    { label: t("nav.explore"), href: "/search", icon: Car },
    { label: t("nav.myTrips"), href: "/trips", icon: Calendar, protected: true },
    { label: t("nav.messages"), href: "/inbox", icon: MessageSquare, protected: true },
  ];

  const hostLinks = [
    { label: t("host.dashboard"), href: "/host/dashboard", icon: LayoutDashboard },
    { label: t("host.listings"), href: "/host/listings", icon: List },
    { label: t("host.calendar"), href: "/host/calendar", icon: Calendar },
    { label: t("host.analytics"), href: "/host/analytics", icon: BarChart3 },
  ];

  return (
    <nav className="sticky top-0 z-50 glass-nav">
      <div className="container-main py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img
              src="/assets/rivones-logo.jpg"
              alt="Rivones"
              className="w-9 h-9 rounded-lg object-cover"
            />
            <span className="font-heading font-bold text-lg text-[#111111] hidden sm:block">
              Rivones
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) =>
              !link.protected || isAuthenticated ? (
                <Link
                  key={link.href}
                  to={link.href}
                  className="px-4 py-2 text-neutral-600 hover:text-[#111111] hover:bg-neutral-100 rounded-lg transition-colors text-sm font-medium"
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {link.label}
                </Link>
              ) : null
            )}
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-2">
            <LanguageSwitcher className="hidden sm:inline-flex" />
            {isAuthenticated ? (
              <>
                <NotificationsBell />
                {/* Host/Admin Dropdown */}
                {(user?.role === "host" || user?.role === "admin") && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="hidden md:flex gap-2 text-sm" data-testid="host-menu-btn">
                        <PremiumIcon icon={LayoutDashboard} variant="glass" tone="terracotta" size="xs" animate={false} />
                        <span>Host</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      {hostLinks.map((link) => (
                        <DropdownMenuItem key={link.href} onClick={() => navigate(link.href)}>
                          <link.icon className="w-4 h-4 mr-2" />
                          {link.label}
                        </DropdownMenuItem>
                      ))}
                      {user?.role === "admin" && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => navigate("/admin")}>
                            <Settings className="w-4 h-4 mr-2" />
                            Admin Panel
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {/* User Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2 h-10 px-2" data-testid="user-menu-btn">
                      {user?.picture ? (
                        <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-[#111111] flex items-center justify-center text-white font-medium text-sm">
                          {user?.name?.charAt(0) || "U"}
                        </div>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-3 py-2 border-b">
                      <p className="font-medium text-sm">{user?.name}</p>
                      <p className="text-xs text-neutral-500">{user?.email}</p>
                    </div>
                    <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="profile-menu-item">
                      <User className="w-4 h-4 mr-2" />
                      {t("nav.profile")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/trips")} data-testid="trips-menu-item">
                      <Calendar className="w-4 h-4 mr-2" />
                      {t("nav.myTrips")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/inbox")} data-testid="inbox-menu-item">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      {t("nav.messages")}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-menu-item">
                      <LogOut className="w-4 h-4 mr-2" />
                      {t("nav.logout")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="hidden sm:flex text-sm" data-testid="login-btn">
                    {t("nav.login")}
                  </Button>
                </Link>
                <Link to="/login">
                  <Button className="btn-primary text-sm py-2.5 px-5" data-testid="signup-btn">
                    {t("nav.signup")}
                  </Button>
                </Link>
              </>
            )}

            {/* Mobile Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden" data-testid="mobile-menu-btn">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 p-0">
                <div className="flex flex-col h-full">
                  <div className="p-6 border-b">
                    <div className="flex items-center gap-3">
                      <img
                        src="/assets/rivones-logo.jpg"
                        alt="Rivones"
                        className="w-9 h-9 rounded-lg object-cover"
                      />
                      <span className="font-heading font-bold">Rivones</span>
                    </div>
                  </div>
                  
                  <div className="flex-1 py-4">
                    <div className="px-6 mb-4 sm:hidden">
                      <LanguageSwitcher />
                    </div>
                    {navLinks.map((link) =>
                      !link.protected || isAuthenticated ? (
                        <Link
                          key={link.href}
                          to={link.href}
                          onClick={() => setMobileMenuOpen(false)}
                          className="flex items-center gap-3 px-6 py-3 hover:bg-neutral-50 transition-colors"
                        >
                          <link.icon className="w-5 h-5 text-neutral-500" />
                          <span className="font-medium">{link.label}</span>
                        </Link>
                      ) : null
                    )}
                    
                    {isAuthenticated && (user?.role === "host" || user?.role === "admin") && (
                      <>
                        <div className="my-3 mx-6 border-t" />
                        <p className="px-6 py-2 text-xs uppercase tracking-wider text-neutral-400 font-medium">Host</p>
                        {hostLinks.map((link) => (
                          <Link
                            key={link.href}
                            to={link.href}
                            onClick={() => setMobileMenuOpen(false)}
                            className="flex items-center gap-3 px-6 py-3 hover:bg-neutral-50 transition-colors"
                          >
                            <link.icon className="w-5 h-5 text-neutral-500" />
                            <span className="font-medium">{link.label}</span>
                          </Link>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
}
